import React, { useState, useRef, useEffect } from 'react';
import { useI18n, type Locale } from '../i18n';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { trackLanguageChange } from '../lib/analytics';

export const LanguageSwitcher: React.FC = () => {
  const { locale, setLocale, availableLocales } = useI18n();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentLocale = availableLocales.find((l) => l.code === locale) || availableLocales[0];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSelect = (code: Locale) => {
    setLocale(code);
    setIsOpen(false);
    trackLanguageChange(code);
  };

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-navy-850 hover:bg-slate-200 dark:hover:bg-navy-800 border border-slate-300 dark:border-navy-700 text-xs font-medium text-slate-800 dark:text-slate-200 hover:text-slate-950 dark:hover:text-white transition-colors focus:outline-none focus:ring-1 focus:ring-gold-500 shadow-sm"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        <Globe className="w-3.5 h-3.5 text-gold-600 dark:text-gold-400" />
        <span className="flex items-center gap-1.5">
          <span>{currentLocale.flag}</span>
          <span className="font-semibold uppercase tracking-wider">{currentLocale.code}</span>
        </span>
        <ChevronDown className={`w-3 h-3 text-slate-500 dark:text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-1.5 w-36 rounded-lg bg-white dark:bg-navy-850 border border-slate-200 dark:border-navy-700 shadow-2xl py-1 z-50 animate-fade-in backdrop-blur-lg">
          {availableLocales.map((item) => (
            <button
              key={item.code}
              onClick={() => handleSelect(item.code)}
              className={`w-full flex items-center justify-between px-3 py-2 text-xs text-left transition-colors ${
                locale === item.code
                  ? 'bg-gold-500/10 text-gold-600 dark:text-gold-400 font-semibold'
                  : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-navy-800 hover:text-slate-950 dark:hover:text-white'
              }`}
            >
              <span className="flex items-center gap-2">
                <span className="text-sm">{item.flag}</span>
                <span>{item.label}</span>
              </span>
              {locale === item.code && <Check className="w-3.5 h-3.5 text-gold-600 dark:text-gold-400" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
